import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";
import { XAutoComplete } from "share-components/mui-wrap/auto-complete/x-auto-complete";
import {
  PaperMaterialFilterKey,
  PaperMaterialFilterSelection,
  usePaperMaterialFilterSelection,
} from "./use-paper-material-group-selection";

type PaperMaterialGroupSelectcionProps = {
  codeSelected: string;
  specificationSizeSelected: string;
  brandSelected: string;
  rollNumberSelected: string;
  typeSelected: string;
  gsmSelected: string;
  setSelectionProp: (prop: PaperMaterialFilterKey, value: string) => void;
  visibleFields?: PaperMaterialFilterKey[];
};

const DEFAULT_VISIBLE_FIELDS: PaperMaterialFilterKey[] = [
  "code",
  "specificationSize",
  "brand",
  "rollNumber",
  "type",
  "gsm",
];

export const PaperMaterialGroupSelectcion = observer(
  ({
    codeSelected,
    specificationSizeSelected,
    brandSelected,
    rollNumberSelected,
    typeSelected,
    gsmSelected,
    setSelectionProp,
    visibleFields = DEFAULT_VISIBLE_FIELDS,
  }: PaperMaterialGroupSelectcionProps) => {
    const selection = useMemo<PaperMaterialFilterSelection>(
      () => ({
        code: codeSelected,
        specificationSize: specificationSizeSelected,
        brand: brandSelected,
        rollNumber: rollNumberSelected,
        type: typeSelected,
        gsm: gsmSelected,
      }),
      [
        brandSelected,
        codeSelected,
        gsmSelected,
        rollNumberSelected,
        specificationSizeSelected,
        typeSelected,
      ],
    );

    const {
      searchCodeOptions,
      searchSpecificationSizeOptions,
      searchBrandOptions,
      searchRollNumberOptions,
      searchTypeOptions,
      searchGsmOptions,
      getValidSelectionAfterChange,
    } = usePaperMaterialFilterSelection(selection);

    const selectionChangedHandler = useCallback(
      (key: PaperMaterialFilterKey, value: string) => {
        const nextSelection = getValidSelectionAfterChange(key, value);

        (Object.keys(nextSelection) as PaperMaterialFilterKey[]).forEach(
          (selectionKey) => {
            if (nextSelection[selectionKey] === selection[selectionKey]) return;
            setSelectionProp(selectionKey, nextSelection[selectionKey]);
          },
        );
      },
      [getValidSelectionAfterChange, selection, setSelectionProp],
    );

    return (
      <>
        {visibleFields.includes("code") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Mã số"
              value={codeSelected}
              searchFunc={searchCodeOptions}
              searchOnFocus={true}
              searchWhenRender={true}
              onValueChanged={(v) => {
                selectionChangedHandler("code", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("specificationSize") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Khổ giấy"
              value={specificationSizeSelected}
              searchFunc={searchSpecificationSizeOptions}
              searchOnFocus={true}
              searchWhenRender={true}
              onValueChanged={(v) => {
                selectionChangedHandler("specificationSize", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("brand") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Hiệu"
              value={brandSelected}
              searchFunc={searchBrandOptions}
              searchOnFocus={true}
              searchWhenRender={true}
              onValueChanged={(v) => {
                selectionChangedHandler("brand", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("rollNumber") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Số cuồn"
              value={rollNumberSelected}
              searchFunc={searchRollNumberOptions}
              searchOnFocus={true}
              searchWhenRender={true}
              onValueChanged={(v) => {
                selectionChangedHandler("rollNumber", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("type") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="Loại giấy"
              value={typeSelected}
              searchFunc={searchTypeOptions}
              searchOnFocus={true}
              searchWhenRender={true}
              onValueChanged={(v) => {
                selectionChangedHandler("type", v?.value ?? "");
              }}
            />
          </Box>
        )}

        {visibleFields.includes("gsm") && (
          <Box sx={{ minWidth: 220 }}>
            <XAutoComplete
              label="GSM"
              value={gsmSelected}
              searchFunc={searchGsmOptions}
              searchOnFocus={true}
              searchWhenRender={true}
              onValueChanged={(v) => {
                selectionChangedHandler("gsm", v?.value ?? "");
              }}
            />
          </Box>
        )}
      </>
    );
  },
);
